import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Plus, Trash2, Vote, Power } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/apiClient";
import { useSSE } from "@/lib/useSSE";
import { EmptyState } from "@/components/dashboard/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

export default function EventPolls() {
  const { event } = useOutletContext();
  const [polls, setPolls] = useState([]);
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [type, setType] = useState("single");
  const [options, setOptions] = useState(["", ""]);

  const load = () => api.get(`/events/${event.id}/polls`).then(({ data }) => setPolls(data));
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [event.id]);
  useSSE(`/stream/event/${event.id}`, (m) => { if (m.type === "polls") load(); });

  const create = async (e) => {
    e.preventDefault();
    const opts = options.map((o) => o.trim()).filter(Boolean);
    if (opts.length < 2) return toast.error("Add at least two options");
    try {
      await api.post(`/events/${event.id}/polls`, { question, type, options: opts });
      toast.success("Poll created");
      setQuestion(""); setType("single"); setOptions(["", ""]); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggle = async (pid) => { await api.put(`/events/${event.id}/polls/${pid}/toggle`); load(); };
  const remove = async (pid) => { await api.delete(`/events/${event.id}/polls/${pid}`); toast.success("Poll deleted"); load(); };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-display text-lg font-bold text-foreground">Polls</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-md" data-testid="add-poll-btn"><Plus className="h-4 w-4 mr-1.5" /> New poll</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create poll</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4" data-testid="poll-form">
              <div><Label>Question</Label><Input value={question} onChange={(e) => setQuestion(e.target.value)} required className="mt-1.5" data-testid="poll-question" /></div>
              <div><Label>Type</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger className="mt-1.5" data-testid="poll-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="single">Single choice</SelectItem><SelectItem value="multiple">Multiple choice</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Options</Label>
                {options.map((o, i) => (
                  <Input key={i} value={o} onChange={(e) => setOptions(options.map((x, j) => (j === i ? e.target.value : x)))} placeholder={`Option ${i + 1}`} data-testid={`poll-option-${i}`} />
                ))}
                <Button type="button" variant="outline" size="sm" className="rounded-md" onClick={() => setOptions([...options, ""])}>+ Add option</Button>
              </div>
              <DialogFooter><Button type="submit" className="rounded-md" data-testid="poll-submit">Create poll</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {polls.length === 0 ? (
        <EmptyState icon={Vote} title="No polls yet" description="Create live polls to gauge the room in real time." />
      ) : (
        <div className="space-y-4" data-testid="polls-list">
          {polls.map((p) => {
            const total = p.options.reduce((a, o) => a + o.votes, 0) || 1;
            return (
              <div key={p.id} className="rounded-xl border border-border bg-card p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display font-bold text-foreground">{p.question}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5 capitalize">{p.type} choice · {p.responses || 0} responses</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => toggle(p.id)} data-testid={`toggle-poll-${p.id}`}
                      className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border ${p.is_open ? "border-accent/30 text-accent bg-accent/10" : "border-border text-muted-foreground"}`}>
                      <Power className="h-3 w-3" /> {p.is_open ? "Open" : "Closed"}
                    </button>
                    <button onClick={() => remove(p.id)} className="text-muted-foreground hover:text-destructive p-1"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
                <div className="mt-4 space-y-3">
                  {p.options.map((o) => (
                    <div key={o.id}>
                      <div className="flex justify-between text-sm mb-1"><span className="text-foreground">{o.text}</span><span className="text-muted-foreground">{Math.round((o.votes / total) * 100)}% · {o.votes}</span></div>
                      <Progress value={(o.votes / total) * 100} className="h-2" />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
